'use client'

import { useMemo, useReducer, useState } from 'react'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import { CartItem } from 'modules/cart'
import { extractTokenFromClientSession } from 'modules/customer'
import { IS_TMS_DELIVERY } from 'shared/config'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { returnSaleOrder } from '../api'
import {
  SaleOrder,
  SaleOrderReturnRequestBody,
  SaleOrderReturnStatus
} from '../types'

interface UseOrderReturnModalProps {
  order: SaleOrder
  onSubmitted?: () => void
}

type SelectedItemsState = Record<string, number>

type SelectedItemsAction =
  | { type: 'TOGGLE_ITEM'; id: string; checked: boolean; maxQuantity: number }
  | {
      type: 'CHANGE_ITEM_QUANTITY'
      id: string
      delta: number
      maxQuantity: number
    }

export const MAX_REASON_NOTE_LENGTH = 500

const removeItemFromSelection = (state: SelectedItemsState, id: string) => {
  if (!(id in state)) return state

  const nextState = { ...state }

  delete nextState[id]

  return nextState
}

const selectedItemsReducer = (
  state: SelectedItemsState,
  action: SelectedItemsAction
): SelectedItemsState => {
  switch (action.type) {
    case 'TOGGLE_ITEM':
      if (!action.checked) return removeItemFromSelection(state, action.id)
      if (action.maxQuantity < 1) return state
      if (state[action.id] === action.maxQuantity) return state

      return { ...state, [action.id]: action.maxQuantity }
    case 'CHANGE_ITEM_QUANTITY': {
      const currentValue = state[action.id] || 0

      if (!currentValue && action.delta < 1) return state

      const nextValue = Math.min(
        action.maxQuantity,
        Math.max(0, currentValue + action.delta)
      )

      if (nextValue === currentValue) return state

      if (nextValue < 1) return removeItemFromSelection(state, action.id)

      return { ...state, [action.id]: nextValue }
    }
    default:
      return state
  }
}

export const useSaleOrderReturnModal = ({
  order,
  onSubmitted
}: UseOrderReturnModalProps) => {
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const [selectedItems, dispatchSelectedItems] = useReducer(
    selectedItemsReducer,
    {}
  )
  const [reasonNote, setReasonNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [open, setOpen] = useState(false)

  const isTmsDelivery = IS_TMS_DELIVERY
  const canSubmit = order.returnStatus === SaleOrderReturnStatus.AVAILABLE
  const saleOrderItems = order.items || []
  const selectedItemsCount = Object.keys(selectedItems).length

  const selectedProductsPayload = useMemo(
    () =>
      Object.entries(selectedItems).map(([id, count]) => ({
        id,
        count
      })),
    [selectedItems]
  )

  const isReasonNoteTooLong = reasonNote.length > MAX_REASON_NOTE_LENGTH

  const handleOpen = () => setOpen(true)

  const handleClose = () => {
    if (isSubmitting) return

    setOpen(false)
  }

  const getItemMaxReturnQuantity = (item: CartItem) =>
    Math.max(0, item.orderedQuantity ?? item.quantity ?? 0)

  const isItemBlockedForReturn = (item: CartItem) => !item.isReturnable

  const handleItemToggle = (item: CartItem, checked: boolean) => {
    if (checked && isItemBlockedForReturn(item)) return

    dispatchSelectedItems({
      type: 'TOGGLE_ITEM',
      id: item.id,
      checked,
      maxQuantity: getItemMaxReturnQuantity(item)
    })
  }

  const changeItemCount = (item: CartItem, delta: number) => {
    if (isItemBlockedForReturn(item)) return

    dispatchSelectedItems({
      type: 'CHANGE_ITEM_QUANTITY',
      id: item.id,
      delta,
      maxQuantity: getItemMaxReturnQuantity(item)
    })
  }

  const handleSelectAllItems = () => {
    saleOrderItems.forEach(item => {
      if (isItemBlockedForReturn(item)) return

      dispatchSelectedItems({
        type: 'TOGGLE_ITEM',
        id: item.id,
        checked: true,
        maxQuantity: getItemMaxReturnQuantity(item)
      })
    })
  }

  const resetSelectedItems = () => {
    for (const item of saleOrderItems) {
      dispatchSelectedItems({
        type: 'TOGGLE_ITEM',
        id: item.id,
        checked: false,
        maxQuantity: getItemMaxReturnQuantity(item)
      })
    }
  }

  const handleSubmit = async () => {
    const body: SaleOrderReturnRequestBody = {
      items: selectedProductsPayload.map(item => ({
        id: item.id,
        quantity: item.count
      })),
      reasonNote: reasonNote.trim() || undefined
    }

    try {
      setIsSubmitting(true)

      await returnSaleOrder({
        id: order.id,
        token,
        body
      })

      toast.success('Wniosek zwrotu został wysłany.')
      resetSelectedItems()
      setReasonNote('')
      onSubmitted?.()
      setOpen(false)
    } catch (error) {
      reportSentryError(error, 'Failed to return order')
      toast.error(getApiErrors(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    canSubmit,
    changeItemCount,
    getItemMaxReturnQuantity,
    handleClose,
    handleItemToggle,
    handleOpen,
    handleSelectAllItems,
    handleSubmit,
    isReasonNoteTooLong,
    isSubmitting,
    isTmsDelivery,
    open,
    reasonNote,
    saleOrderItems,
    selectedItems,
    selectedItemsCount,
    setReasonNote
  }
}
